import { useState, useEffect } from "react";
import { GenerateVizaButton } from "./GenerateVizaButton";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Badge } from "./ui/badge";
import { Loader2, ClipboardList, CheckCircle, XCircle, Sparkles, X as CloseIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { AttachmentsSection } from "./AttachmentsSection";
import { patientApi } from "../services/api";
import { toast } from "sonner";

const STATUS_LABEL = {
  neinceput: { label: "Neînceput", color: "bg-slate-100 text-slate-700" },
  in_progres: { label: "În progres", color: "bg-blue-100 text-blue-800" },
  completat: { label: "Completat", color: "bg-emerald-100 text-emerald-800" },
};

/**
 * Atelier 5 — Analize medicale
 *  - Upload PDF-uri cu analize
 *  - Concluzii (text)
 *  - Recomandări (text)
 */
export const Atelier5Button = ({ patientId, patientData, atelierData, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [completat, setCompletat] = useState(false);
  const [concluzii, setConcluzii] = useState("");
  const [recomandari, setRecomandari] = useState("");
  const [acteMedicaleNecesare, setActeMedicaleNecesare] = useState("");
  const [analizeInLimiteNormale, setAnalizeInLimiteNormale] = useState(false);
  const [analizeFiles, setAnalizeFiles] = useState([]);
  const [aiLoadingId, setAiLoadingId] = useState(null);
  const [aiResult, setAiResult] = useState(null);   // { filename, values, abnormalities, summary, recommendations, warnings }
  // Sindroame biologice — flag-uri de top + sub-checkbox-uri (populează Recomandări în Concluzii)
  const [sindroame, setSindroame] = useState({
    sindrom_citoliza_hepatica: false,
    sch_in_observatie: false,
    sch_deconditionare_temporara: false,
    sch_control_transaminaze: false,
    sch_investigatii_suplimentare: false,
    sindrom_inflamator_biologic: false,
    sib_dispensarizare_mf: false,
    sib_efort_adaptat: false,
    sib_in_observatie: false,
    sindrom_rhabdomioliza: false,
    sr_repeta_ck: false,
    sr_in_observatie: false,
    sr_deconditionare_totala: false,
    sindrom_anemic: false,
    sa_in_observatie: false,
    sa_dispensarizare_mf: false,
    sa_limitare_eforturi: false,
  });
  const setSindromField = (k, v) => setSindroame((prev) => ({ ...prev, [k]: !!v }));

  useEffect(() => {
    if (open) {
      setCompletat(atelierData?.completat || false);
      setConcluzii(atelierData?.concluzii || "");
      setRecomandari(atelierData?.recomandari || "");
      setActeMedicaleNecesare(atelierData?.acte_medicale_necesare || "");
      setAnalizeInLimiteNormale(!!atelierData?.analize_in_limite_normale);
      setAnalizeFiles(atelierData?.analize_files || []);
      setAiResult(null);
      setSindroame({
        sindrom_citoliza_hepatica: !!atelierData?.sindrom_citoliza_hepatica,
        sch_in_observatie: !!atelierData?.sch_in_observatie,
        sch_deconditionare_temporara: !!atelierData?.sch_deconditionare_temporara,
        sch_control_transaminaze: !!atelierData?.sch_control_transaminaze,
        sch_investigatii_suplimentare: !!atelierData?.sch_investigatii_suplimentare,
        sindrom_inflamator_biologic: !!atelierData?.sindrom_inflamator_biologic,
        sib_dispensarizare_mf: !!atelierData?.sib_dispensarizare_mf,
        sib_efort_adaptat: !!atelierData?.sib_efort_adaptat,
        sib_in_observatie: !!atelierData?.sib_in_observatie,
        sindrom_rhabdomioliza: !!atelierData?.sindrom_rhabdomioliza,
        sr_repeta_ck: !!atelierData?.sr_repeta_ck,
        sr_in_observatie: !!atelierData?.sr_in_observatie,
        sr_deconditionare_totala: !!atelierData?.sr_deconditionare_totala,
        sindrom_anemic: !!atelierData?.sindrom_anemic,
        sa_in_observatie: !!atelierData?.sa_in_observatie,
        sa_dispensarizare_mf: !!atelierData?.sa_dispensarizare_mf,
        sa_limitare_eforturi: !!atelierData?.sa_limitare_eforturi,
      });
    }
  }, [open, atelierData]);

  const currentStatus = atelierData?.completat
    ? STATUS_LABEL.completat
    : atelierData?.data_completare || atelierData?.concluzii || atelierData?.recomandari || (atelierData?.analize_files || []).length > 0
    ? STATUS_LABEL.in_progres
    : STATUS_LABEL.neinceput;

  const handleUploadAnaliza = async (file, onProgress) => {
    const att = await patientApi.uploadAtelier5Analiza(patientId, file, onProgress);
    setAnalizeFiles((prev) => [...prev, att]);
    return att;
  };

  const handleDeleteAnaliza = async (fileId) => {
    await patientApi.deleteAtelier5Analiza(patientId, fileId);
    setAnalizeFiles((prev) => prev.filter((a) => a.id !== fileId));
  };

  const handleViewAnaliza = async (att) => {
    const url = patientApi.atelier5AnalizaViewUrl(patientId, att.id);
    const res = await fetch(url, { credentials: "include" });
    if (!res.ok) throw new Error("Nu s-a putut deschide analiza.");
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const w = window.open(objectUrl, "_blank");
    setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
    if (!w) {
      toast.warning("Popup-ul a fost blocat. Verifică setările browser-ului.");
    }
  };

  const handleAiAnalyze = async (att) => {
    setAiLoadingId(att.id);
    setAiResult(null);
    toast.info(`AI: analizez „${att.filename}”... (poate dura 10-30s)`);
    try {
      const result = await patientApi.aiAnalyzeAnaliza(patientId, att.id);
      setAiResult(result);
      toast.success("Analiza AI finalizată.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Analiza AI a eșuat.";
      toast.error(msg);
    } finally {
      setAiLoadingId(null);
    }
  };

  const appendToField = (setter, current, added) => {
    if (!added) return;
    const sep = current.trim() ? "\n\n" : "";
    setter(`${current}${sep}${added}`.trim());
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const payload = {
        completat,
        concluzii,
        recomandari,
        acte_medicale_necesare: acteMedicaleNecesare,
        analize_in_limite_normale: analizeInLimiteNormale,
        data_completare: completat ? new Date().toISOString() : atelierData?.data_completare || "",
        ...sindroame,
      };
      const updated = await patientApi.updateAtelier5(patientId, payload);
      toast.success("Atelier 5 salvat cu succes");
      if (onUpdate) onUpdate(updated);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la salvare Atelier 5");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="atelier-5-btn"
          className="w-full justify-between gap-2 border-slate-300 hover:bg-slate-50"
        >
          <div className="flex items-center gap-2">
            <ClipboardList className="w-4 h-4" />
            <span>Atelier 5</span>
          </div>
          <Badge className={currentStatus.color}>{currentStatus.label}</Badge>
        </Button>
      </DialogTrigger>

      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="atelier-5-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            Atelier 5 — Analize medicale
            {atelierData?.completat ? (
              <CheckCircle className="w-4 h-4 text-emerald-600" />
            ) : (
              <XCircle className="w-4 h-4 text-slate-400" />
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Upload analize medicale (PDF-uri) */}
          <AttachmentsSection
            title="Încarcă analizele medicale"
            description="PDF-uri sau imagini (JPG/PNG/HEIC) — analize de sânge, imagistică, buletine de laborator. Max 10 fișiere × 10 MB. Apasă butonul ✨ pentru extragere automată AI a valorilor de laborator."
            attachments={analizeFiles}
            onUpload={handleUploadAnaliza}
            onDelete={handleDeleteAnaliza}
            onView={handleViewAnaliza}
            onAiAnalyze={handleAiAnalyze}
            aiLoadingId={aiLoadingId}
            canUpload={true}
            canDelete={true}
            emptyText="Nu ai încărcat încă nicio analiză."
            testIdPrefix="atelier-5-analize"
          />

          {/* Rezultatul analizei AI (dacă există) */}
          {aiResult && (
            <Card className="border-violet-200 bg-violet-50/40" data-testid="atelier-5-ai-result">
              <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0">
                <div>
                  <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Work Sans', sans-serif" }}>
                    <Sparkles className="w-4 h-4 text-violet-600" />
                    Rezultat AI — „{aiResult.filename}”
                  </CardTitle>
                  <p className="text-xs text-muted-foreground mt-1">
                    {(aiResult.values || []).length} valori extrase • {(aiResult.abnormalities || []).length} abateri detectate
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => setAiResult(null)}
                  title="Închide"
                >
                  <CloseIcon className="w-4 h-4" />
                </Button>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Warnings critice */}
                {(aiResult.warnings || []).length > 0 && (
                  <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-900">
                    <p className="font-semibold mb-1">⚠ Atenționări critice</p>
                    <ul className="list-disc pl-5 space-y-0.5">
                      {aiResult.warnings.map((w, i) => (<li key={i}>{w}</li>))}
                    </ul>
                  </div>
                )}

                {/* Tabel valori */}
                {(aiResult.values || []).length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b text-left text-muted-foreground">
                          <th className="py-1 pr-2">Analiză</th>
                          <th className="py-1 pr-2">Valoare</th>
                          <th className="py-1 pr-2">Referință</th>
                          <th className="py-1">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aiResult.values.map((v, i) => {
                          const st = (v.status || "unknown").toLowerCase();
                          const badge = st === "high"
                            ? { cls: "bg-red-100 text-red-800", icon: <TrendingUp className="w-3 h-3" />, label: "Crescut" }
                            : st === "low"
                              ? { cls: "bg-blue-100 text-blue-800", icon: <TrendingDown className="w-3 h-3" />, label: "Scăzut" }
                              : st === "normal"
                                ? { cls: "bg-emerald-100 text-emerald-800", icon: <Minus className="w-3 h-3" />, label: "Normal" }
                                : { cls: "bg-slate-100 text-slate-700", icon: null, label: "N/A" };
                          return (
                            <tr key={i} className="border-b last:border-0">
                              <td className="py-1 pr-2 font-medium">{v.name}</td>
                              <td className="py-1 pr-2 font-mono">{v.value} {v.unit}</td>
                              <td className="py-1 pr-2 text-muted-foreground">{v.reference_range || "-"}</td>
                              <td className="py-1">
                                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${badge.cls}`}>
                                  {badge.icon}{badge.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Abateri */}
                {(aiResult.abnormalities || []).length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Abateri semnificative</p>
                    <ul className="list-disc pl-5 text-sm space-y-0.5">
                      {aiResult.abnormalities.map((a, i) => (<li key={i}>{a}</li>))}
                    </ul>
                  </div>
                )}

                {/* Summary + Recomandari + acțiuni */}
                {aiResult.summary && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Concluzii generate</p>
                    <p className="text-sm whitespace-pre-line bg-white/60 rounded p-2 border">{aiResult.summary}</p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      onClick={() => appendToField(setConcluzii, concluzii, aiResult.summary)}
                      data-testid="atelier-5-ai-add-concluzii"
                    >
                      + Adaugă la Concluzii
                    </Button>
                  </div>
                )}
                {aiResult.recommendations && (
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground mb-1">Recomandări generate</p>
                    <p className="text-sm whitespace-pre-line bg-white/60 rounded p-2 border">{aiResult.recommendations}</p>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="mt-2"
                      onClick={() => appendToField(setRecomandari, recomandari, aiResult.recommendations)}
                      data-testid="atelier-5-ai-add-recomandari"
                    >
                      + Adaugă la Recomandări
                    </Button>
                  </div>
                )}

                <p className="text-[11px] text-muted-foreground italic">
                  ℹ Rezultatele AI sunt orientative. Validează întotdeauna concluziile clinice înainte de a le salva în fișă.
                </p>
              </CardContent>
            </Card>
          )}

          <div className="space-y-2">
            <Label htmlFor="concluzii-5">Concluzii</Label>
            <Textarea
              id="concluzii-5"
              data-testid="atelier-5-concluzii"
              value={concluzii}
              onChange={(e) => setConcluzii(e.target.value)}
              placeholder="Notează concluziile pe baza analizelor medicale..."
              rows={5}
            />
          </div>

          {/* Marker pozitiv global — analize normale */}
          <div className="flex items-center gap-2 p-3 rounded-lg border border-emerald-200 bg-emerald-50/40">
            <Checkbox
              id="analize_in_limite_normale"
              checked={analizeInLimiteNormale}
              onCheckedChange={(v) => setAnalizeInLimiteNormale(!!v)}
              data-testid="atelier-5-analize-normale"
            />
            <Label htmlFor="analize_in_limite_normale" className="text-sm font-medium cursor-pointer">
              Analize medicale de laborator în limite normale
            </Label>
          </div>

          {/* Sindroame biologice — sub-recomandările bifate populează Recomandări în Concluzii aviz medico-sportiv */}
          <Card className="border-amber-200 bg-amber-50/30" data-testid="atelier-5-sindroame-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                Recomandări sindroame biologice
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Bifează sindromul detectat pe analize; sub-elementele bifate apar automat în &bdquo;Recomandări&rdquo; → Stare de sănătate/Antrenament.
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* 1) Sindrom de citoliza hepatica */}
              <div className="rounded-md border bg-white p-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="sindrom_citoliza_hepatica"
                    checked={sindroame.sindrom_citoliza_hepatica}
                    onCheckedChange={(v) => setSindromField("sindrom_citoliza_hepatica", v)}
                    data-testid="a5-sch"
                  />
                  <Label htmlFor="sindrom_citoliza_hepatica" className="text-sm font-semibold cursor-pointer">
                    Sindrom de citoliza hepatica (TGO/TGP crescute)
                  </Label>
                </div>
                {sindroame.sindrom_citoliza_hepatica && (
                  <div className="ml-6 mt-2 space-y-1.5">
                    {[
                      ["sch_in_observatie", "În observație"],
                      ["sch_deconditionare_temporara", "Decondiționare temporară de efort intens/susținut (reducerea încărcării)"],
                      ["sch_control_transaminaze", "Control transaminaze peste 15 zile"],
                      ["sch_investigatii_suplimentare", "Necesită investigații suplimentare"],
                    ].map(([k, lbl]) => (
                      <div key={k} className="flex items-start gap-2">
                        <Checkbox
                          id={k}
                          checked={sindroame[k]}
                          onCheckedChange={(v) => setSindromField(k, v)}
                          data-testid={`a5-${k.replace(/_/g, "-")}`}
                        />
                        <Label htmlFor={k} className="text-sm cursor-pointer">{lbl}</Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 2) Sindrom inflamator biologic */}
              <div className="rounded-md border bg-white p-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="sindrom_inflamator_biologic"
                    checked={sindroame.sindrom_inflamator_biologic}
                    onCheckedChange={(v) => setSindromField("sindrom_inflamator_biologic", v)}
                    data-testid="a5-sib"
                  />
                  <Label htmlFor="sindrom_inflamator_biologic" className="text-sm font-semibold cursor-pointer">
                    Sindrom inflamator biologic (VSH crescut)
                  </Label>
                </div>
                {sindroame.sindrom_inflamator_biologic && (
                  <div className="ml-6 mt-2 space-y-1.5">
                    {[
                      ["sib_dispensarizare_mf", "Dispensarizare prin medicul de familie (pentru investigații suplimentare și tratament)"],
                      ["sib_efort_adaptat", "Efort adaptat (evitarea eforturilor maximale până la identificarea etiologică)"],
                      ["sib_in_observatie", "În observație"],
                    ].map(([k, lbl]) => (
                      <div key={k} className="flex items-start gap-2">
                        <Checkbox
                          id={k}
                          checked={sindroame[k]}
                          onCheckedChange={(v) => setSindromField(k, v)}
                          data-testid={`a5-${k.replace(/_/g, "-")}`}
                        />
                        <Label htmlFor={k} className="text-sm cursor-pointer">{lbl}</Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 3) Sindrom de rhabdomioliza de suprasolicitare */}
              <div className="rounded-md border bg-white p-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="sindrom_rhabdomioliza"
                    checked={sindroame.sindrom_rhabdomioliza}
                    onCheckedChange={(v) => setSindromField("sindrom_rhabdomioliza", v)}
                    data-testid="a5-sr"
                  />
                  <Label htmlFor="sindrom_rhabdomioliza" className="text-sm font-semibold cursor-pointer">
                    Sindrom de rhabdomioliza de suprasolicitare (hiperCKemie izolata de efort)
                  </Label>
                </div>
                {sindroame.sindrom_rhabdomioliza && (
                  <div className="ml-6 mt-2 space-y-1.5">
                    {[
                      ["sr_repeta_ck", "Repetă dozare CK la 15 zile"],
                      ["sr_in_observatie", "În observație"],
                      ["sr_deconditionare_totala", "Decondiționare totală de efort (3-5 zile), urmată de reintroducerea treptată a efortului aerobic, hidratare orală susținută 3-4 ore pe zi (protecție renală); reînceperea activității susținute (conform programului de antrenament) se va face numai după normalizarea CK-ului"],
                    ].map(([k, lbl]) => (
                      <div key={k} className="flex items-start gap-2">
                        <Checkbox
                          id={k}
                          checked={sindroame[k]}
                          onCheckedChange={(v) => setSindromField(k, v)}
                          data-testid={`a5-${k.replace(/_/g, "-")}`}
                        />
                        <Label htmlFor={k} className="text-sm cursor-pointer">{lbl}</Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 4) Sindrom anemic / suspiciune beta-talasemie minoră */}
              <div className="rounded-md border bg-white p-3">
                <div className="flex items-start gap-2">
                  <Checkbox
                    id="sindrom_anemic"
                    checked={sindroame.sindrom_anemic}
                    onCheckedChange={(v) => setSindromField("sindrom_anemic", v)}
                    data-testid="a5-sa"
                  />
                  <Label htmlFor="sindrom_anemic" className="text-sm font-semibold cursor-pointer">
                    Sindrom anemic / suspiciune beta-talasemie minoră
                  </Label>
                </div>
                {sindroame.sindrom_anemic && (
                  <div className="ml-6 mt-2 space-y-1.5">
                    {[
                      ["sa_in_observatie", "În observație"],
                      ["sa_dispensarizare_mf", "Dispensarizare prin medicul de familie (pentru investigații suplimentare, inițierea tratamentului)"],
                      ["sa_limitare_eforturi", "Limitarea eforturilor de mare intensitate, anaerobe, până la corectarea anemiei"],
                    ].map(([k, lbl]) => (
                      <div key={k} className="flex items-start gap-2">
                        <Checkbox
                          id={k}
                          checked={sindroame[k]}
                          onCheckedChange={(v) => setSindromField(k, v)}
                          data-testid={`a5-${k.replace(/_/g, "-")}`}
                        />
                        <Label htmlFor={k} className="text-sm cursor-pointer">{lbl}</Label>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <div className="space-y-2">
            <Label htmlFor="recomandari-5">Recomandări</Label>
            <Textarea
              id="recomandari-5"
              data-testid="atelier-5-recomandari"
              value={recomandari}
              onChange={(e) => setRecomandari(e.target.value)}
              placeholder="Recomandările medicale pentru sportiv..."
              rows={5}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="acte-medicale-necesare-5">Acte medicale necesare de adus</Label>
            <Textarea
              id="acte-medicale-necesare-5"
              data-testid="atelier-5-acte-medicale-necesare"
              value={acteMedicaleNecesare}
              onChange={(e) => setActeMedicaleNecesare(e.target.value)}
              placeholder="Ex: rezultat ecografie abdominală, spirometrie recentă, aviz cardiolog..."
              rows={4}
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap items-center gap-3 pt-4 border-t">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="completat-5"
                data-testid="atelier-5-completat"
                checked={completat}
                onCheckedChange={(v) => setCompletat(!!v)}
              />
              <Label htmlFor="completat-5" className="text-sm font-medium cursor-pointer">
                Marchează ca și completat
              </Label>
            </div>
            <div className="flex-1" />
            <GenerateVizaButton patient={patientData} onGenerated={onUpdate} />
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
              Anulează
            </Button>
            <Button onClick={handleSave} disabled={isLoading} data-testid="atelier-5-save">
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvează
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
